import { type ColumnDef } from "@tanstack/react-table";
import { type FC, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  FilterTabs,
  ReactTableComponent,
  Typography,
  TypographyVariant,
} from "../../../../../components/ui-kit";
import {
  TableContainer,
  TableHeader,
  TableHeaderLeft,
} from "../../dashboard/sharedTableStyles";
import {
  formatMinorUnits,
  PAYABLE_ENTITY_TAB_LABELS,
  type PriceCatalogueProfile,
  type PriceCatalogueUsageTier,
} from "../types";

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`;

const ProfileBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type TierRow = PriceCatalogueUsageTier & {
  id: string;
  bandLabel: string;
};

interface TieredPriceProfilesTableProps {
  profiles: PriceCatalogueProfile[];
}

const ProfileTierTable: FC<{ profile: PriceCatalogueProfile }> = ({
  profile,
}) => {
  const rules = profile.usagePricing ?? [];
  const tabs = useMemo(
    () =>
      (profile.usagePricing ?? []).map((rule) => ({
        name:
          PAYABLE_ENTITY_TAB_LABELS[rule.payableEntityType] ||
          rule.payableEntityType,
        value: rule.payableEntityType,
      })),
    [profile.usagePricing]
  );
  const [activeType, setActiveType] = useState(tabs[0]?.value || "");

  useEffect(() => {
    if (!tabs.some((tab) => tab.value === activeType)) {
      setActiveType(tabs[0]?.value || "");
    }
  }, [activeType, tabs]);

  const activeRule =
    rules.find((rule) => rule.payableEntityType === activeType) || rules[0];

  const rows: TierRow[] = useMemo(() => {
    const tiers = activeRule?.tiers ?? [];
    const byTxCount =
      String(activeRule?.pricingMetric || "").toLowerCase() ===
      "transaction_count";
    return tiers.map((tier, index) => {
      const previousUpTo = index > 0 ? tiers[index - 1]?.upTo : null;
      const bandLabel =
        tier.upTo == null
          ? previousUpTo != null
            ? byTxCount
              ? `Above ${previousUpTo.toLocaleString("en-GB")} transactions`
              : `Above ${previousUpTo.toLocaleString("en-GB")}`
            : "Open-ended"
          : byTxCount
          ? `Up to ${tier.upTo.toLocaleString("en-GB")} transactions`
          : `Up to ${tier.upTo.toLocaleString("en-GB")}`;
      return {
        ...tier,
        id: `${profile.id}-${activeRule?.payableEntityType}-${index}`,
        bandLabel,
      };
    });
  }, [activeRule, profile.id]);

  const columns = useMemo<ColumnDef<TierRow>[]>(
    () => [
      {
        accessorKey: "bandLabel",
        header:
          String(activeRule?.pricingMetric || "").toLowerCase() ===
          "transaction_count"
            ? "Transaction band"
            : "Volume band",
        cell: ({ row }) => row.original.bandLabel,
      },
      {
        accessorKey: "flatFeeMinorUnits",
        header: "Flat fee",
        cell: ({ row }) =>
          formatMinorUnits(
            row.original.flatFeeMinorUnits,
            profile.billingCurrency
          ),
      },
      {
        accessorKey: "unitFeeDecimal",
        header: "Unit fee",
        cell: ({ row }) => row.original.unitFeeDecimal || "—",
      },
    ],
    [activeRule?.pricingMetric, profile.billingCurrency]
  );

  return (
    <ProfileBlock>
      <TableContainer>
        <TableHeader>
          <TableHeaderLeft>
            <Typography variant={TypographyVariant.BODY4}>
              {profile.label || profile.key}
            </Typography>
            <Typography variant={TypographyVariant.BODY2}>
              {String(profile.billingCurrency || "").toUpperCase()}
              {profile.interval ? ` · ${profile.interval}` : ""}
            </Typography>
          </TableHeaderLeft>
        </TableHeader>
        {tabs.length > 0 && (
          <FilterTabs
            key={`${profile.id}-${tabs.map((tab) => tab.value).join("-")}`}
            menus={tabs}
            startWithIndex={Math.max(
              0,
              tabs.findIndex((tab) => tab.value === activeType)
            )}
            clickHandler={(value) => setActiveType(String(value || ""))}
          />
        )}
        <ReactTableComponent data={rows as any} columns={columns} />
      </TableContainer>
    </ProfileBlock>
  );
};

const TieredPriceProfilesTable: FC<TieredPriceProfilesTableProps> = ({
  profiles,
}) => {
  if (!profiles.length) return null;

  return (
    <Section>
      <Typography variant={TypographyVariant.HEADER2}>
        Usage-tiered profiles
      </Typography>
      {profiles.map((profile) => (
        <ProfileTierTable key={profile.id} profile={profile} />
      ))}
    </Section>
  );
};

export default TieredPriceProfilesTable;
